import React from 'react';
import { FileText, CheckCircle, Shield, TrendingDown, Clock, Phone, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const ContratsEntretienQuai = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="Contrats d'Entretien des Quais de Chargement | ORION INDUSTRIE"
        description="Contrats de maintenance préventive pour niveleurs de quai et portes industrielles. Visites programmées, traçabilité, budget maîtrisé et possibilité de mensualisation."
        keywords="contrat entretien quai, maintenance préventive niveleur, contrat maintenance porte industrielle, entretien programmé quai chargement, contrat annuel niveleur"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#020d2a] to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-12 h-12 text-[#FF6600]" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Contrats d'Entretien des Quais
            </h1>
          </div>
          <p className="text-2xl text-orange-400 font-semibold mb-4">
            Maintenance programmée • Budget maîtrisé • Tranquillité assurée
          </p>
          <p className="text-xl text-slate-300 max-w-4xl mb-8">
            Planifiez l'entretien de vos niveleurs, portes et équipements de quai avec un contrat adapté à votre activité. 
            Interventions programmées, traçabilité complète et possibilité de mensualisation.
          </p>
        </div>
      </section>

      {/* Pourquoi un contrat */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Pourquoi souscrire un contrat d'entretien ?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: <Shield className="w-10 h-10 text-blue-600" />,
                title: 'Prévention des pannes',
                desc: 'Identifiez les anomalies avant qu\'elles ne deviennent des pannes coûteuses et bloquantes'
              },
              {
                icon: <Clock className="w-10 h-10 text-orange-600" />,
                title: 'Prolongation de la durée de vie',
                desc: 'Maintenez vos équipements en bon état et optimisez leur longévité'
              },
              {
                icon: <TrendingDown className="w-10 h-10 text-green-600" />,
                title: 'Maîtrise du budget',
                desc: 'Budget annuel défini, sans surprise, avec possibilité de règlement mensuel'
              },
              {
                icon: <FileText className="w-10 h-10 text-purple-600" />,
                title: 'Traçabilité complète',
                desc: 'Historique des interventions, rapports détaillés et suivi des observations'
              },
              {
                icon: <CheckCircle className="w-10 h-10 text-teal-600" />,
                title: 'Planification assurée',
                desc: 'Visites programmées selon votre activité, sans interruption de votre exploitation'
              },
              {
                icon: <AlertCircle className="w-10 h-10 text-red-600" />,
                title: 'Sécurité renforcée',
                desc: 'Contrôles réguliers des dispositifs de sécurité et mise en conformité'
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types de contrats */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Nos Formules de Contrat
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contrat 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-blue-500 transition-colors">
              <div className="text-center mb-6">
                <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Essentiel
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Contrat Entretien Niveleur
                </h3>
                <p className="text-slate-600">Pour un ou plusieurs niveleurs</p>
              </div>

              <div className="space-y-3 mb-8">
                {[
                  'Révision annuelle programmée',
                  'Inspection complète selon config',
                  'Contrôles hydrauliques',
                  'Graissage et lubrification',
                  'Rapport détaillé',
                  'Identification des pièces à remplacer',
                  'Déplacements inclus'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600">
                  <strong>Fréquence recommandée :</strong> 1 à 2 visites/an selon utilisation
                </p>
              </div>

              <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Demander un devis
              </Button>
            </div>

            {/* Contrat 2 */}
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 shadow-xl border-2 border-[#FF6600]">
              <div className="text-center mb-6">
                <div className="inline-block bg-[#FF6600] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  ⭐ Recommandé
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Pack Maintenance Quai
                </h3>
                <p className="text-slate-600">Niveleurs + Portes</p>
              </div>

              <div className="space-y-3 mb-8">
                {[
                  'Entretien niveleurs de quai',
                  'Maintenance portes sectionnelles',
                  'Maintenance rideaux métalliques',
                  'Contrôles de sécurité',
                  'Rapports d\'intervention',
                  'Historique centralisé',
                  'Interventions coordonnées',
                  'Déplacements mutualisés'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF6600] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-4 mb-6 border border-[#FF6600]">
                <p className="text-sm text-slate-600">
                  <strong>Avantage :</strong> Un seul interlocuteur pour l'ensemble de vos postes de chargement
                </p>
              </div>

              <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-[#FF6600] hover:bg-[#E65A00] text-white"
              >
                Demander un devis
              </Button>
            </div>

            {/* Contrat 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-purple-500 transition-colors">
              <div className="text-center mb-6">
                <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Premium
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Maintenance Complète
                </h3>
                <p className="text-slate-600">Installation logistique</p>
              </div>

              <div className="space-y-3 mb-8">
                {[
                  'Tous les équipements du Pack',
                  'Butoirs et protections',
                  'Abris et sas de quai',
                  'Équipements périphériques',
                  'Planning annuel personnalisé',
                  'Priorité dépannage',
                  'Référent technique dédié',
                  'Possibilité de mensualisation'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600">
                  <strong>Pour qui :</strong> Plateformes logistiques avec installations complètes
                </p>
              </div>

              <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                Demander un devis
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Ce qui est inclus */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Ce qui est Inclus dans nos Contrats
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                Inclus
              </h3>
              <ul className="space-y-3">
                {[
                  'Visites de maintenance programmées',
                  'Inspections et contrôles prévus',
                  'Graissage et lubrification',
                  'Essais fonctionnels',
                  'Contrôles de sécurité',
                  'Rapports d\'intervention détaillés',
                  'Identification des anomalies',
                  'Recommandations techniques',
                  'Déplacements programmés',
                  'Traçabilité complète'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-orange-500" />
                Non inclus (sur devis)
              </h3>
              <ul className="space-y-3">
                {[
                  'Pièces détachées',
                  'Remplacement de pièces usées',
                  'Appoints d\'huile',
                  'Réparations exceptionnelles',
                  'Dépannages d\'urgence',
                  'Interventions hors contrat',
                  'Modifications d\'installation',
                  'Mise en conformité majeure'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-orange-500">•</span>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-lg mt-6">
                <p className="text-sm text-slate-700">
                  Les interventions correctives et pièces font l'objet d'un devis séparé après diagnostic.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Traçabilité */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Suivi et Traçabilité de vos Équipements
              </h2>
              <p className="text-xl text-slate-600">
                Chaque intervention est documentée et tracée pour assurer un suivi optimal
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Après chaque intervention</h3>
                  <ul className="space-y-3">
                    {[
                      'Autocollant de traçabilité posé sur l\'équipement',
                      'Date de révision indiquée',
                      'Référence du rapport d\'intervention',
                      'Prochain entretien préconisé',
                      'Identification claire de l\'équipement'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Rapport d'intervention</h3>
                  <ul className="space-y-3">
                    {[
                      'Prestations réalisées détaillées',
                      'Observations et anomalies constatées',
                      'Points d\'attention',
                      'Recommandations techniques',
                      'Pièces à prévoir',
                      'Échéance prochain entretien'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <FileText className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg mt-8">
                <p className="text-slate-700">
                  <strong className="text-slate-900">Évolution future :</strong> Consultation en ligne des rapports via QR code. 
                  Cette fonctionnalité sera proposée ultérieurement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Obligations réglementaires */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Entretien Préventif et Obligations Réglementaires
            </h2>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg mb-8">
              <h3 className="font-bold text-slate-900 mb-3">Important à savoir</h3>
              <div className="space-y-3 text-slate-700">
                <p>
                  Les obligations réglementaires et les périodicités de maintenance diffèrent selon le type d'équipement.
                </p>
                <p>
                  <strong>Une révision préventive ne constitue pas automatiquement une certification de conformité.</strong> 
                  Certains contrôles et vérifications spécifiques peuvent nécessiter l'intervention d'organismes agréés.
                </p>
                <p>
                  Nos contrats d'entretien visent à maintenir vos équipements en bon état de fonctionnement et à identifier 
                  les anomalies avant qu'elles ne deviennent critiques.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-8">
              <h3 className="font-bold text-slate-900 mb-4">Fréquence d'entretien recommandée</h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF6600]">•</span>
                  <span><strong>Niveleurs de quai :</strong> Révision annuelle minimum, semestrielle si usage intensif</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF6600]">•</span>
                  <span><strong>Portes sectionnelles :</strong> Selon normes applicables et conditions d'utilisation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-bold text-[#FF6600]">•</span>
                  <span><strong>Rideaux métalliques :</strong> Selon réglementation et environnement</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-gradient-to-r from-[#020d2a] to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Prêt à sécuriser l'entretien de vos quais ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Obtenez un devis de contrat adapté à vos équipements et votre activité
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/#quote'}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Demander un devis de contrat
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContratsEntretienQuai;